import {
  Download,
  ExternalLink,
  GitCompare,
  GitMerge,
  Link as LinkIcon,
  MessageSquare,
  MoreVertical,
  Send,
  Wrench,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../../lib/auth-context.js';
import { Button } from '../../../components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu.js';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip.js';
import { useScenarioDetail } from '../hooks.js';
import type { ScenarioRow as ScenarioRowData } from '../types.js';
import { FusionRequestSheet } from './FusionRequestSheet.js';
import { ScenarioAiSummarySheet } from './ScenarioAiSummarySheet.js';
import { ScenarioCompareCodeDialog } from './ScenarioCompareCodeDialog.js';
import { ScenarioCompareEventSubscriptionDialog } from './ScenarioCompareEventSubscriptionDialog.js';

/**
 * Right-most actions cell. Mirrors the legacy
 * `wf-audit/src/slices/scenario/components/scenario/DefaultTable.tsx`
 * trailing column composition:
 *   • Comment icon (Fusion Request) — **always shown, in every view mode
 *     including Business** (legacy's `FusionRequestProvider` was the
 *     first child of the cell, ungated)
 *   • AI Tech Summary icon — Admin + Developer, gated on
 *     `aiTechnicalSummary` being present (legacy's `showDevLink`
 *     covered admin AND developer)
 *   • Actions dropdown (Compare + Scenario groups) — Admin + Developer
 *
 * All three buttons are functional:
 *   • Comment icon opens a `FusionRequestSheet` that POSTs to
 *     `/v1/fusion-requests` → engine → public Workfront Fusion
 *     webhook (Jira-issue creation downstream).
 *   • AI Tech Summary icon opens a `ScenarioAiSummarySheet` showing
 *     the markdown-rendered summary with Copy + Word + PDF export
 *     (client-side, jsPDF + docx).
 *   • Actions dropdown's `Scenario Code` opens the blueprint-compare
 *     dialog seeded with the row's fusionId on the left pane;
 *     `Event Subscription` opens the Workfront event-sub compare
 *     dialog. `Open Scenario` + `Workfront Version Control` are
 *     external Fusion links.
 *
 * Pre-2026-05-18 the Fusion Request button was hidden in Business
 * mode because the parent column rendered conditionally
 * (`showActions = role !== 'business'`). Operator-reported bug;
 * fix: always render the Actions column with at least the Comment
 * button; `showActionsDropdown` still gates AI Summary + dropdown.
 */
export interface ScenarioActionsCellProps {
  row: ScenarioRowData;
  showActionsDropdown: boolean;
}

export function ScenarioActionsCell({
  row,
  showActionsDropdown,
}: ScenarioActionsCellProps): JSX.Element {
  const [compareCodeOpen, setCompareCodeOpen] = useState(false);
  const [compareEventSubOpen, setCompareEventSubOpen] = useState(false);
  const [fusionRequestOpen, setFusionRequestOpen] = useState(false);
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);

  // Lazy-fetch the AI technical summary only when the operator
  // opens the sheet — the list response stopped shipping the full
  // multi-KB markdown to keep the per-row payload small. The detail
  // endpoint returns it on its full row, so we gate the fetch on
  // `aiSummaryOpen` and read off `detail.aiTechnicalSummary` when it
  // settles. Demo mode short-circuits via `row.aiTechnicalSummary`
  // (the fixture still pre-populates the field) so the showcase
  // sheet renders without a network hop.
  const hasAiSummary = Boolean(row.hasAiTechnicalSummary || row.aiTechnicalSummary);
  const { data: detailForSummary } = useScenarioDetail(
    aiSummaryOpen && !row.aiTechnicalSummary ? row.fusionId : undefined,
  );
  const aiSummaryText =
    row.aiTechnicalSummary ??
    (typeof detailForSummary?.aiTechnicalSummary === 'string'
      ? (detailForSummary.aiTechnicalSummary as string)
      : '');

  return (
    <div className="flex items-center justify-center gap-1.5">
      <CommentIconButton onClick={() => setFusionRequestOpen(true)} />
      {showActionsDropdown ? (
        <>
          {hasAiSummary ? <AiSummaryIconButton onClick={() => setAiSummaryOpen(true)} /> : null}
          <ActionsDropdown
            row={row}
            onOpenCompareCode={() => setCompareCodeOpen(true)}
            onOpenCompareEventSub={() => setCompareEventSubOpen(true)}
          />
        </>
      ) : null}
      <FusionRequestSheet
        open={fusionRequestOpen}
        onOpenChange={setFusionRequestOpen}
        scenarioId={row.fusionId}
        scenarioName={row.name}
      />
      {hasAiSummary ? (
        <ScenarioAiSummarySheet
          open={aiSummaryOpen}
          onOpenChange={setAiSummaryOpen}
          title={`Technical Steps for "${row.name ?? row.fusionId}"`}
          text={aiSummaryText || 'Loading…'}
        />
      ) : null}
      {showActionsDropdown ? (
        <>
          <ScenarioCompareCodeDialog
            open={compareCodeOpen}
            onOpenChange={setCompareCodeOpen}
            defaultFusionId={row.fusionId}
          />
          <ScenarioCompareEventSubscriptionDialog
            open={compareEventSubOpen}
            onOpenChange={setCompareEventSubOpen}
            defaultFusionId={row.fusionId}
          />
        </>
      ) : null}
    </div>
  );
}

function CommentIconButton({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-foreground/60 hover:bg-foreground/10 hover:text-foreground"
            onClick={onClick}
            aria-label="Fusion Request"
          >
            <MessageSquare className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Make a Fusion Enhancement Request</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function AiSummaryIconButton({ onClick }: { onClick: () => void }): JSX.Element {
  // Wrench glyph matches legacy `wf-audit/.../DefaultTable.tsx` —
  // operators read "technical steps" as a wrench (tooling /
  // engineering) and "executive overview" as the sparkles glyph
  // (rendered in the Description column on `ScenarioRow.tsx`).
  // The two icons together make the business-vs-technical split
  // visible at a glance without needing tooltip hover.
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-foreground/60 hover:bg-foreground/10 hover:text-foreground"
            onClick={onClick}
            aria-label="AI Technical Summary"
          >
            <Wrench className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Show Technical Steps</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function ActionsDropdown({
  row,
  onOpenCompareCode,
  onOpenCompareEventSub,
}: {
  row: ScenarioRowData;
  onOpenCompareCode: () => void;
  onOpenCompareEventSub: () => void;
}): JSX.Element {
  const url = row.url ?? null;
  const { api } = useAuth();
  const [downloading, setDownloading] = useState(false);

  async function downloadBlueprintForScenario(): Promise<void> {
    if (downloading) return;
    setDownloading(true);
    try {
      // Two-step fetch (mirrors the compare-code dialog flow):
      //   1. `/v1/scenarios/{id}/blueprints` returns the version
      //      list. Fusion's API requires a specific `blueprintId`
      //      to fetch the JSON — there's no "give me the latest"
      //      shortcut.
      //   2. Pick the newest version, fetch its JSON via
      //      `/v1/scenarios/{id}/blueprint?blueprintId=<n>`, save.
      const versionsResp = await api.get<{
        blueprints: ReadonlyArray<{ id?: number; version?: number; created?: string }>;
      }>(`/v1/scenarios/${encodeURIComponent(row.fusionId)}/blueprints`);
      const versions = versionsResp.blueprints ?? [];
      if (versions.length === 0) {
        toast.error('No blueprint versions found for this scenario.');
        return;
      }
      // The engine returns `{ id, version, created, comment }` per
      // version (Fusion API v3 has no separate blueprint id: `id` is the
      // version number, which is also what `blueprintId` means on the
      // detail endpoint). Mirrors `versionKey()` in
      // ScenarioCompareCodeDialog.tsx, which falls back to `v.version`
      // when `v.id` is missing.
      const latest = [...versions]
        .filter((v): v is { version: number } & typeof v => typeof v.version === 'number')
        .sort((a, b) => b.version - a.version)[0];
      const blueprintId = latest?.id ?? latest?.version;
      if (!latest || blueprintId === undefined) {
        toast.error(
          'Blueprint versions missing both id and version fields — engine response shape changed?',
        );
        return;
      }
      const r = await api.get<{ blueprint: unknown }>(
        `/v1/scenarios/${encodeURIComponent(row.fusionId)}/blueprint?blueprintId=${blueprintId}`,
      );
      const json = JSON.stringify(r.blueprint, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const objectUrl = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = objectUrl;
        const safe = (row.name || 'blueprint').replace(/[^\w.-]+/g, '_').slice(0, 80);
        a.download = `${safe}-${row.fusionId}-v${latest.version ?? blueprintId}.json`;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    } catch (err) {
      toast.error(`Blueprint download failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setDownloading(false);
    }
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-foreground/60 hover:bg-foreground/10 hover:text-foreground"
          aria-label="More actions"
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]">
        <DropdownMenuLabel>Compare</DropdownMenuLabel>
        <DropdownMenuGroup>
          <DropdownMenuItem onSelect={onOpenCompareCode}>
            <GitCompare className="h-4 w-4" />
            <span>Scenario Code</span>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={onOpenCompareEventSub}>
            <Send className="h-4 w-4" />
            <span>Event Subscription</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Scenario</DropdownMenuLabel>
        <DropdownMenuGroup>
          {url ? (
            <DropdownMenuItem asChild>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center gap-2"
              >
                <LinkIcon className="h-4 w-4" />
                <span>Open Scenario</span>
                <ExternalLink className="ml-auto h-3 w-3 text-foreground/40" />
              </a>
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            disabled={downloading}
            onSelect={(e) => {
              e.preventDefault();
              void downloadBlueprintForScenario();
            }}
          >
            <Download className="h-4 w-4" />
            <span>{downloading ? 'Downloading…' : 'Download Blueprint'}</span>
          </DropdownMenuItem>
          {row.versionsLink ? (
            <DropdownMenuItem asChild>
              <a
                // `versionsLink` points at the **Workfront** document
                // backing the scenario's blueprint backup —
                // e.g. `https://{tenant}.my.workfront.com/document/{id}/allVersions`.
                // The `workfront-backup` Fargate consumer uploads each
                // blueprint as a new version of that document and
                // stores the URL on the scenario row. Operator-reported:
                // pre-fix this opened `${fusionUrl}/versions` (Fusion's
                // own version page), which was wrong — operators want
                // the Workfront document's revision history.
                href={row.versionsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center gap-2"
              >
                <GitMerge className="h-4 w-4" />
                <span>Workfront Version Control</span>
                <ExternalLink className="ml-auto h-3 w-3 text-foreground/40" />
              </a>
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
